import numeral from "numeral";
import React, { useCallback, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useBalanceStore } from "../../_store/balanceStore";
import { useEducationStore } from "../../_store/educationStore";
import { useFuturePlanStore } from "../../_store/futurePlanStore";
import { useRetireStore } from "../../_store/retireStore";
import { useStrategyStore } from "../../_store/strategyRetireStore";
import { useUserStore } from "../../_store/userStore";
import { MTButton } from "../../components/button/MTButton";
import { HeaderTitle } from "../../components/header/Header";
import Container from "../../components/layout/Container";
import NavigateGoBack from "../../functions/Navigate.Goback";
import {
  DebtRetire,
  InvestmentReturnCal,
  InvestmentReturnNormalCal,
  OutcomeChildEducation,
  OutcomeDebt,
  OutcomeNormalCal,
  OutcomeReliableCal,
  OutcomeUreliableCal,
} from "../../functions/retirement/retirementFunction";
import {
  InvestmentReturnCalAfter,
  InvestmentReturnCalAfterNormal,
  InvestmentReturnCalSensitive,
  InvestmentReturnCalSensitiveNormal,
  OutcomeReliableCalAfter,
  OutcomeUreliableCalAfter,
  OutcomeUreliableCalAfterNormal,
} from "../../functions/retirement/retirementFunctionAfter";
import { calDebtCreditCard } from "../../utils/useFunctions";
import {
  STYLE_MARGIN_TOP_PARENT,
  STYLE_WHITE_TABLE_RETIRE,
} from "../../utils/useStyle";
import { key_under_education } from "../education/functions/options";
import {
  dataAge,
  dataAgeAF,
  loopIncome,
  loopIncomeAF,
  loopIncomeOther,
  loopInvestmentAssets,
  loopInvestmentAssetsAF,
  loopOutcome,
  loopOutcomeAF,
  loopRemain,
  loopRemainAF,
  loopReturnInvestment,
  loopReturnInvestmentAF,
} from "./functions/functionRetriementPlan";
import useExportCSV from "../../utils/useExportCsv";
import {
  exportToExcelBefore,
  exportToExcelAfter,
  exportToExcelSensitive,
  generateFileName,
} from "./functions/excelExportFunctions";

const TABLE_HEADER_STYLE =
  " px-[16px] pt-[14px] pb-[6px] break-keep whitespace-nowrap text-[#18488A] text-base font-semibold font-IBMPlexSansThai";

let merge_keys = [
  "debt_short_list",
  "debt_creditcard",
  "debt_shortterm",
  "debt_informal_shortterm",
  "debt_shortterm_other",
  "debt_car",
  "debt_place",
  "debt_long_other",
];

const merge_label_key = {
  expenese_home_items: "บ้าน",
  expenese_car_items: "ซื้อรถ",
  expenese_children_items: "ลูก",
  expenese_travel_items: "ท่องเที่ยว",
  expenese_collection_items: "ของสะสม",
  expenese_other_items: "รายจ่ายอื่นๆ",
  income_legacy_items: "รายรับจากมรดก",
  income_business_items: "ขายธุรกิจ",
  income_property_items: "รายรับจากอสังหาริม",
  income_other_items: "รายรับอื่นๆ",
  income_peryear: "รายรับ",
  remain_accumulate: "สินทรัพย์ลงทุน",
  outcome_peryear: "จ่ายผันแปร",
  out_come_stable: "จ่ายคงที่ (ไม่รวมหนี้)",
  outcome_total: "รวมรายจ่ายต่อปี ",
  pension_peryear: "บำนาญต่อปี",
  remain: "เงินคงเหลือ",
  income_investment: "ผลตอบแทนการลงทุน ",
  debt_shortterm_other: "หนี้ดอกลอย",
  debt_informal_shortterm: "หนี้นอกระบบ",
  debt_shortterm: "หนี้ระยะสั้น",
  debt_creditcard: "หนี้บัตรเครดิต",
  expenese_children_education: "การศึกษาบุตร",
  debt_car: "ยานพาหนะ",
  debt_place: "ที่อยู่อาศัย",
  debt_long_other: "หนี้ระยะยาวอื่นๆ",
};

const SummaryTableFinance = ({}) => {
  const location = useLocation();

  const isBeforeMode = location?.search === "?before";
  const isAfterMode = location?.search === "?after";
  const isSensitive = location?.search === "?sensitive";

  const balanceData = useBalanceStore((state) => state.balanceData);
  const futurePlanData = useFuturePlanStore((state) => state.futurePlanData);
  const educationData = useEducationStore((state) => state.educationData);
  const userData = useUserStore((state) => state.userData);
  const strategy = useStrategyStore((state) => state.strategy);

  const retireData = useRetireStore((state) => state.retireData);
  const setRetireData = useRetireStore((state) => state.setRetireData);
  const clearRetireData = useRetireStore((state) => state.clearRetireData);

  const strategyLists = strategy?.map((item) => item?.key);

  const navigate = useNavigate();

  const tempFutureData = useMemo(() => {
    let copyFuturePlanData = futurePlanData;
    let calExpense = DebtRetire(
      futurePlanData,
      balanceData,
      educationData,
      key_under_education
    );

    copyFuturePlanData = { ...copyFuturePlanData, cash_flow_items: calExpense };
    return copyFuturePlanData;
  }, []);

  useEffect(() => {
    let ignore = false;
    if (!ignore) {
      setRetireData({
        ...retireData,
        is_plan: true,
        bf_plan_age: dataAge(),
        bf_plan_income: loopIncome(),
        bf_plan_income_other: loopIncomeOther(),
        bf_plan_outcome: loopOutcome(),
        bf_plan_remain: loopRemain(),
        bf_return_investment: loopReturnInvestment(),
        bf_investment_assets: loopInvestmentAssets(),

        af_plan_age: strategyLists?.includes("strategy_age_dead")
          ? dataAgeAF()
          : dataAge(),
        af_plan_income: loopIncomeAF(),
        af_plan_income_other: loopIncomeOther(),
        af_plan_outcome: loopOutcomeAF(),
        af_plan_remain: loopRemainAF(),
        af_return_investment: strategyLists?.includes(
          "strategy_assets_generate_per_year"
        )
          ? loopReturnInvestmentAF()
          : loopReturnInvestment(),
        af_investment_assets: strategyLists?.includes(
          "strategy_assets_generate_per_year"
        )
          ? loopInvestmentAssetsAF()
          : loopInvestmentAssets(),
      });
    }

    return () => {
      ignore = true;
    };
  }, [
    setRetireData,
    retireData?.strategy_out_come,
    retireData?.strategy_income,
    retireData?.strategy_passive_income_retire,
    retireData?.strategy_assets_generate,
    retireData?.strategy_assets_generate_per_year,
    retireData?.strategy_salary,
    retireData?.strategy_salary_peryear,
    retireData?.strategy_control_rate_outcome,
    retireData?.strategy_age_retire,
    retireData?.strategy_age_dead,
    retireData?.strategy_investment_bf_retirement,
    retireData?.strategy_investment_af_retirement,
  ]);

  const dataPlan = useMemo(() => {
    if (isBeforeMode) {
      if (userData?.is_premium) {
        return InvestmentReturnCal(
          retireData,
          balanceData,
          [],
          educationData
        )?.map((item) => {
          return item?.asset_invest;
        });
      }
      return InvestmentReturnNormalCal(retireData)?.map((item) => {
        return item?.asset_invest;
      });
    }
    if (isAfterMode) {
      if (userData?.is_premium) {
        return InvestmentReturnCalAfter(
          retireData,
          strategy,
          balanceData,
          educationData
        )?.map((item) => {
          return item?.asset_invest;
        });
      }
      return InvestmentReturnCalAfterNormal(retireData, strategy)?.map(
        (item) => {
          return item?.asset_invest;
        }
      );
    }
    if (isSensitive) {
      if (userData?.is_premium) {
        return InvestmentReturnCalSensitive(
          retireData,
          strategy,
          balanceData
        )?.map((item) => {
          return item?.asset_invest;
        });
      }
      return InvestmentReturnCalSensitiveNormal(retireData, strategy)?.map(
        (item) => {
          return item?.asset_invest;
        }
      );
    }
  }, [retireData]);

  const checkColor = useCallback(
    (i) => {
      const green = [];
      const yellow = [];
      const red = [];

      for (let index = 0; index < dataPlan?.length; index++) {
        const currentValue = dataPlan[index];
        const nextValue = dataPlan[index + 1];

        //จุดแรก
        if (index === 0) {
          if (dataPlan[index] < 0) {
            red.push(dataPlan[index]);
            yellow.push(null);
            green.push(null);
          } else {
            if (dataPlan[0] > dataPlan[1]) {
              yellow.push(dataPlan[index]);
              red.push(null);
              green.push(null);
            } else {
              green.push(dataPlan[index]);
              yellow.push(null);
              red.push(null);
            }
          }
          continue;
        }

        // red
        if (dataPlan[index] <= 0) {
          if (nextValue > currentValue && currentValue < 0 && nextValue > 0) {
            red.push(dataPlan[index]);
            green.push(dataPlan[index]);
            yellow.push(null);
            continue;
          }
          red.push(dataPlan[index]);
          yellow.push(null);
          green.push(null);
          continue;
        }

        if (dataPlan[index] < dataPlan[index - 1]) {
          // yellow => green
          if (nextValue > currentValue) {
            green.push(dataPlan[index]);
            yellow.push(dataPlan[index]);
            // yellow.push(null);
            red.push(null);
            continue;
          }

          //yellow => red
          if (nextValue < currentValue && nextValue < 0) {
            red.push(dataPlan[index]);
            green.push(null);
            yellow.push(dataPlan[index]);
            // yellow.push(null);
            continue;
          }

          yellow.push(dataPlan[index]);
          green.push(null);
          red.push(null);
        } else {
          //green => yellow

          if (nextValue < currentValue && nextValue > 0) {
            red.push(null);
            green.push(dataPlan[index]);
            yellow.push(dataPlan[index]);
            // yellow.push(null);
            continue;
          }
          //green
          green.push(dataPlan[index]);
          yellow.push(null);
          red.push(null);
        }
      }

      if (green[i] !== null) {
        return "bg-greenopacity border-maingreen/20 border ";
      }

      if (yellow[i] !== null) {
        return "bg-yellowopacity border-mainyellow/20 border ";
      }
      return "bg-redopacity border-mainred/20 border";
    },
    [dataPlan, retireData]
  );

  let findPercent = strategy?.find(
    (item) => item?.key === "strategy_control_rate_outcome"
  );

  let investmentReturnCal = [
    ...InvestmentReturnCal(retireData, balanceData, [], educationData),
  ];

  let outcomeUreliableCal = [...OutcomeUreliableCal(retireData)];
  let outcomeReliableCal = [...OutcomeReliableCal(retireData, balanceData)];
  let outcomeDebt = [...OutcomeDebt(retireData, balanceData)];
  let outcomeChildEducation = [
    ...OutcomeChildEducation(retireData, educationData),
  ];

  let investmentReturnCalAF = [
    ...InvestmentReturnCalAfter(
      retireData,
      strategy,
      balanceData,
      educationData
    ),
  ];

  let investmentReturnCalSensitive = [
    ...InvestmentReturnCalSensitive(
      retireData,
      strategy,
      balanceData,
      educationData
    ),
  ];

  // console.log("investmentReturnCalAF",investmentReturnCalAF)

  let outcomeUreliableCalAF = useMemo(
    () => [...OutcomeUreliableCalAfter(retireData, educationData, strategy)],
    [retireData, strategy]
  );

  let outcomeReliableCalAF = [...OutcomeReliableCalAfter(retireData, strategy)];
  let outcomeDebtAF = [...OutcomeDebt(retireData, balanceData, strategy)];

  let creditCardPeriod = balanceData?.debt_short_list
    ?.find((item) => item?.options_debt_short === "debt_creditcard")
    ?.credit_cards?.map((item2, index2) => {
      let tempCalDebtCreditCard = calDebtCreditCard(item2);

      tempCalDebtCreditCard.creditor_name = item2?.creditor_name;
      tempCalDebtCreditCard.type_payment = item2?.type_payment;
      tempCalDebtCreditCard.pay_peryear = item2?.pay_peryear;
      tempCalDebtCreditCard.pay_permonth = item2?.pay_permonth;
      return tempCalDebtCreditCard;
    });

  // Excel Export for Before Plan
  const { onClickExport: exportBeforePlan } = useExportCSV({
    fileName: generateFileName(userData, 'before'),
    headers: exportToExcelBefore(
      dataAge(),
      investmentReturnCal,
      retireData,
      outcomeUreliableCal,
      outcomeReliableCal,
      outcomeDebt,
      outcomeChildEducation,
      balanceData,
      creditCardPeriod,
      educationData,
      userData
    )?.headers || [],
    data: exportToExcelBefore(
      dataAge(),
      investmentReturnCal,
      retireData,
      outcomeUreliableCal,
      outcomeReliableCal,
      outcomeDebt,
      outcomeChildEducation,
      balanceData,
      creditCardPeriod,
      educationData,
      userData
    )?.data || [],
    sheetName: "ก่อนวางแผน",
    tableTitle: "ตารางสรุปความเคลื่อนไหวการเงิน (ก่อนวางแผน)",
  });

  // Excel Export for After Plan
  const { onClickExport: exportAfterPlan } = useExportCSV({
    fileName: generateFileName(userData, 'after'),
    headers: exportToExcelAfter(
      retireData,
      investmentReturnCalAF,
      outcomeUreliableCalAF,
      outcomeReliableCalAF,
      outcomeDebtAF,
      outcomeChildEducation,
      balanceData,
      creditCardPeriod,
      educationData,
      userData
    )?.headers || [],
    data: exportToExcelAfter(
      retireData,
      investmentReturnCalAF,
      outcomeUreliableCalAF,
      outcomeReliableCalAF,
      outcomeDebtAF,
      outcomeChildEducation,
      balanceData,
      creditCardPeriod,
      educationData,
      userData
    )?.data || [],
    sheetName: "หลังวางแผน",
    tableTitle: "ตารางสรุปความเคลื่อนไหวการเงิน (หลังวางแผน)",
  });

  // Excel Export for Sensitive Analysis
  const { onClickExport: exportSensitivePlan } = useExportCSV({
    fileName: generateFileName(userData, 'sensitive'),
    headers: exportToExcelSensitive(
      retireData,
      investmentReturnCalSensitive,
      outcomeUreliableCalAF,
      outcomeReliableCalAF,
      outcomeDebtAF,
      outcomeChildEducation,
      balanceData,
      creditCardPeriod,
      educationData,
      userData
    )?.headers || [],
    data: exportToExcelSensitive(
      retireData,
      investmentReturnCalSensitive,
      outcomeUreliableCalAF,
      outcomeReliableCalAF,
      outcomeDebtAF,
      outcomeChildEducation,
      balanceData,
      creditCardPeriod,
      educationData,
      userData
    )?.data || [],
    sheetName: "ทดสอบความอ่อนไหว",
    tableTitle: "ตารางสรุปความเคลื่อนไหวการเงิน (ทดสอบความอ่อนไหว)",
  });

  if (userData?.is_premium) {
    const colorStatusBF = (index) => {
      let sumRemain =
        retireData?.bf_plan_income?.[index] -
        (outcomeUreliableCal[index] +
          outcomeReliableCal[index] +
          outcomeDebt[index]);

      let netInvestment = investmentReturnCal?.[index]?.asset_invest;

      if (netInvestment < 0) {
        return "bg-redopacity border-mainred/20 border ";
      }

      if (netInvestment >= 0 && sumRemain < 0) {
        return "bg-yellowopacity border-mainyellow/20 border ";
      }

      if (netInvestment >= 0 && sumRemain >= 0) {
        // green
        return "bg-greenopacity border-maingreen/20 border ";
      }

      // green
      return "bg-greenopacity border-maingreen/20 border ";
    };

    const colorStatusAF = (index) => {
      let sumRemain =
        retireData?.af_plan_income?.[index] -
        (outcomeUreliableCalAF[index] +
          outcomeReliableCalAF[index] +
          outcomeDebtAF[index]);

      let netInvestment = investmentReturnCalAF?.[index]?.asset_invest;

      if (netInvestment < 0) {
        return "bg-redopacity border-mainred/20 border ";
      }

      if (netInvestment >= 0 && sumRemain < 0) {
        return "bg-yellowopacity border-mainyellow/20 border ";
      }

      if (netInvestment >= 0 && sumRemain >= 0) {
        // green
        return "bg-greenopacity border-maingreen/20 border ";
      }

      // green
      return "bg-greenopacity border-maingreen/20 border ";
    };

    const colorStatusSensitive = (index) => {
      let sumRemain =
        Number(retireData?.af_plan_income?.[index] || 0) -
        (Number(outcomeUreliableCalAF?.[index] || 0) +
          Number(outcomeReliableCalAF?.[index] || 0) +
          Number(outcomeDebtAF?.[index] || 0));

      let netInvestment = Number(
        investmentReturnCalSensitive?.[index]?.asset_invest || 0
      );

      // console.log("index",index)
      // console.log("sumRemain",sumRemain)
      // console.log("netInvestment",netInvestment)

      if (netInvestment < 0) {
        // console.log("red")
        return "bg-redopacity border-mainred/20 border ";
      }

      if (netInvestment >= 0 && sumRemain < 0) {
        // console.log("yellow")
        return "bg-yellowopacity border-mainyellow/20 border ";
      }

      if (netInvestment >= 0 && sumRemain >= 0) {
        // console.log("green")
        // green
        return "bg-greenopacity border-maingreen/20 border ";
      }

      // console.log("green")
      // console.log("------------------------")
      // green
      return "bg-greenopacity border-maingreen/20 border ";
    };

    // console.log(retireData?.ishavekids);
    // console.log("educationData", educationData);

    return (
      <React.Fragment>
        {isBeforeMode ? (
          <Container>
            {/*-------------------- ตารางก่อนวางแผน --------------------*/}
            <div className={`${STYLE_MARGIN_TOP_PARENT} min-h-screen mb-20 `}>
              <HeaderTitle
                title="ตารางสรุปความเคลื่อนไหวการเงิน"
                color="bg-[#EAEAEA]"
                isGoback
                onClickGoBack={() => {
                  NavigateGoBack(navigate);
                }}
              />
              <div className=" w-full h-14 px-6 font-semibold text-mainblue flex items-center ">
                <div className="mr-3">ก่อนวางแผน</div>
                <MTButton
                  onClick={exportBeforePlan}
                  flexnone="flex-none"
                  width="w-fit text-xs"
                  text="Excel"
                  iconleft
                  icon={
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g clip-path="url(#clip0_6711_51468)">
                        <path
                          d="M14.1733 8.54535H11.6287V7.27304H14.1733V8.54535ZM14.1733 9.27235H11.6287V10.5447H14.1733V9.27235ZM14.1733 3.27441H11.6287V4.5467H14.1733V3.27441ZM14.1733 5.27373H11.6287V6.54601H14.1733V5.27373ZM14.1733 11.2717H11.6287V12.544H14.1733V11.2717ZM15.9309 13.7799C15.8582 14.158 15.4038 14.167 15.1003 14.1798H9.44769V15.8156H8.31897L0 14.3615V1.64045L8.36806 0.18457H9.44769V1.63316H14.9058C15.213 1.64588 15.5511 1.62407 15.8182 1.80763C16.0054 2.07663 15.9873 2.41835 16 2.72732L15.9927 12.1895C15.9837 12.7184 16.0418 13.2583 15.9309 13.7799ZM6.66497 10.9282C6.16334 9.91035 5.65263 8.89976 5.15278 7.88191C5.64716 6.89135 6.13428 5.89716 6.61956 4.90295C6.20697 4.92295 5.79438 4.94838 5.38363 4.97748C5.07644 5.72448 4.71838 6.45151 4.47847 7.22398C4.25491 6.49513 3.95866 5.79354 3.68784 5.08288C3.28797 5.10469 2.88809 5.12832 2.48825 5.15195C2.90991 6.08257 3.35887 7.00038 3.76781 7.93645C3.28616 8.84523 2.83541 9.76676 2.36828 10.681C2.76631 10.6973 3.16438 10.7137 3.56241 10.7191C3.84597 9.99576 4.19856 9.29963 4.44575 8.5617C4.6675 9.35416 5.04372 10.0866 5.35272 10.8445C5.79075 10.8755 6.22694 10.9027 6.66497 10.9282ZM15.1331 2.49638H9.44769V3.27441H10.9017V4.5467H9.44769V5.27373H10.9017V6.54601H9.44769V7.27304H10.9017V8.54535H9.44769V9.27235H10.9017V10.5447H9.44769V11.2717H10.9017V12.544H9.44769V13.3845H15.1331V2.49638Z"
                          fill="white"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_6711_51468">
                          <rect width="16" height="16" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  }
                />
              </div>
              {/* //Head */}
              <div className=" px-6">
                <div className="flex w-full ">
                  <div className=" shadow-md rounded-tl-[20px] rounded-bl-[20px] shadow-gray-500 w-fit sticky z-10 ">
                    <div className="  bg-gray50 h-14 px-3 font-semibold text-mainblue rounded-t-[20px]  rounded-tr-none w-12 flex gap-[38px] items-center ">
                      <div>อายุ</div>
                    </div>
                    <div className="  ">
                      <div
                        className={`  flex  items-center text-lg font-medium text-mainblue w-12 h-full `}
                      >
                        <div className=" h-14 w-full">
                          {dataAge()?.map((row, index) => {
                            return (
                              <div
                                key={index}
                                className={` h-full flex justify-center items-center text-mainblue w-full ${colorStatusBF(
                                  index
                                )}`}
                              >
                                {row}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className=" w-full max-w-2xl overflow-hidden overflow-x-scroll  h-full rounded-tr-[20px] rounded-br-[20px] bg-gray50">
                    <div className={`w-fit  `}>
                      <div
                        className={` flex font-semibold text-mainblue items-start w-full 
                    `}
                      >
                        {/* สินทรัพย์เพื่อการลงทุนสุทธิ */}
                        <div className="">
                          <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                            สินทรัพย์เพื่อการลงทุนสุทธิ
                          </div>
                          {investmentReturnCal?.map((row, index) => {
                            return (
                              <div
                                key={index}
                                className={` whitespace-nowrap h-14 flex justify-center items-center px-3 ${colorStatusBF(
                                  index
                                )}`}
                              >
                                {numeral(row?.asset_invest).format("0,0.00")}
                              </div>
                            );
                          })}
                        </div>

                        {/* รวมรายได้ */}
                        <div className="">
                          <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                            รวมรายได้
                          </div>
                          {retireData?.bf_plan_income?.map((row, index) => {
                            return (
                              <div
                                key={index}
                                className={STYLE_WHITE_TABLE_RETIRE + " px-3 "}
                              >
                                {numeral(
                                  Number(
                                    retireData?.bf_plan_income?.[index] || 0
                                  ) +
                                    Number(
                                      retireData?.bf_plan_income_other?.[
                                        index
                                      ] || 0
                                    )
                                ).format("0,0.00")}
                              </div>
                            );
                          })}
                        </div>

                        {/* เงินเดือน */}
                        <div className="">
                          <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                            เงินเดือน
                          </div>
                          {retireData?.bf_plan_income?.map((row, index) => {
                            return (
                              <div
                                key={index}
                                className={STYLE_WHITE_TABLE_RETIRE + " px-3 "}
                              >
                                {numeral(row).format("0,0.00")}
                              </div>
                            );
                          })}
                        </div>

                        {/* รายได้อื่นๆ */}
                        <div className="">
                          <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                            รายได้อื่นๆ
                          </div>
                          {retireData?.bf_plan_income_other?.map(
                            (row, index) => {
                              return (
                                <div
                                  key={index}
                                  className={
                                    STYLE_WHITE_TABLE_RETIRE + " px-3 "
                                  }
                                >
                                  {numeral(row).format("0,0.00")}
                                </div>
                              );
                            }
                          )}
                        </div>

                        {/* รวมรายจ่ายต่อปี */}
                        <div className="">
                          <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                            {userData?.is_premium
                              ? "รวมรายจ่ายต่อปี"
                              : "รายจ่าย"}
                          </div>
                          {retireData?.bf_plan_age?.map((row, index) => {
                            return (
                              <div
                                key={index}
                                className={STYLE_WHITE_TABLE_RETIRE + " px-3 "}
                              >
                                {numeral(
                                  Number(outcomeUreliableCal?.[index] || 0) +
                                    Number(outcomeReliableCal?.[index] || 0) +
                                    Number(outcomeDebt?.[index] || 0) +
                                    (retireData?.ishavekids
                                      ? Number(
                                          outcomeChildEducation?.[index] || 0
                                        )
                                      : 0)
                                ).format("0,0.00")}
                              </div>
                            );
                          })}
                        </div>

                        {/* รายจ่ายผันแปร */}
                        {userData?.is_premium && (
                          <div className="">
                            <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                              รายจ่ายผันแปร
                            </div>
                            {outcomeUreliableCal?.map((row, index) => {
                              return (
                                <div
                                  key={index}
                                  className={
                                    STYLE_WHITE_TABLE_RETIRE + " px-3 "
                                  }
                                >
                                  {numeral(row).format("0,0.00")}
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* รายจ่ายคงที่ */}
                        {userData?.is_premium && (
                          <div className="">
                            <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                              รายจ่ายคงที่
                            </div>
                            {outcomeReliableCal?.map((row, index) => {
                              return (
                                <div
                                  key={index}
                                  className={
                                    STYLE_WHITE_TABLE_RETIRE + " px-3 "
                                  }
                                >
                                  {numeral(row).format("0,0.00")}
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* คงเหลือ */}
                        <div className="">
                          <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                            คงเหลือ
                          </div>
                          {retireData?.bf_plan_income?.map((row, index) => {
                            return (
                              <div
                                key={index}
                                className={STYLE_WHITE_TABLE_RETIRE + " px-3 "}
                              >
                                {numeral(
                                  Number(
                                    retireData?.bf_plan_income?.[index] || 0
                                  ) +
                                    Number(
                                      retireData?.bf_plan_income_other?.[
                                        index
                                      ] || 0
                                    ) -
                                    (Number(outcomeUreliableCal?.[index] || 0) +
                                      Number(outcomeReliableCal?.[index] || 0) +
                                      Number(outcomeDebt?.[index] || 0))
                                ).format("0,0.00")}
                              </div>
                            );
                          })}
                        </div>

                        {/* ผลตอบแทนการลงทุน */}
                        <div className="">
                          <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                            ผลตอบแทนการลงทุน
                          </div>
                          {retireData?.bf_plan_age?.map((row, index) => {
                            return (
                              <div
                                key={index}
                                className={STYLE_WHITE_TABLE_RETIRE + " px-3 "}
                              >
                                {numeral(
                                  investmentReturnCal?.[index]?.return_invest
                                ).format("0,0.00")}
                              </div>
                            );
                          })}
                        </div>

                        {merge_keys?.map((item, index) => {
                          // ========หนี้=============================

                          // หนี้ดอกลอย
                          if (item === "debt_shortterm_other") {
                            return balanceData?.debt_short_list
                              ?.find(
                                (item) =>
                                  item?.options_debt_short ===
                                  "debt_shortterm_other"
                              )
                              ?.short_term_others?.map((item2, index2) => {
                                const calcPeriodInterest = (index) => {
                                  let year = Math.floor(item2?.period / 12);
                                  let lastYearMonth = item2?.period % 12;

                                  if (index >= Math.ceil(item2?.period / 12)) {
                                    return 0;
                                  }
                                  if (index < year) {
                                    return 12;
                                  } else {
                                    return lastYearMonth;
                                  }
                                };
                                return (
                                  <div key={index2} className="flex flex-col">
                                    <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                                      {`${
                                        item2?.creditor_name
                                          ? item2?.creditor_name
                                          : merge_label_key[item]
                                      } (-)`}
                                    </div>
                                    {retireData?.bf_plan_age?.map(
                                      (cash_flow_items, index3) => {
                                        return (
                                          <div
                                            key={index3}
                                            className={
                                              STYLE_WHITE_TABLE_RETIRE +
                                              " px-3 "
                                            }
                                          >
                                            {numeral(
                                              item2?.period === "Infinity"
                                                ? item2?.pay_permonth * 12
                                                : item2?.pay_permonth *
                                                    calcPeriodInterest(index3)
                                            )?.format("0,0.00")}
                                          </div>
                                        );
                                      }
                                    )}
                                  </div>
                                );
                              });
                          }

                          // หนี้ีนอกระบบ
                          if (item === "debt_informal_shortterm") {
                            return balanceData?.debt_short_list
                              ?.find(
                                (item) =>
                                  item?.options_debt_short ===
                                  "debt_informal_shortterm"
                              )
                              ?.informal_short_terms?.map((item2, index2) => {
                                const calcPeriodInterest = (index) => {
                                  let year = Math.floor(
                                    item2?.number_of_days / 365
                                  );
                                  let lastYearMonth =
                                    item2?.number_of_days % 30;

                                  if (
                                    index >=
                                    Math.ceil(item2?.number_of_days / 365)
                                  ) {
                                    return 0;
                                  }
                                  if (index < year) {
                                    return 365;
                                  } else {
                                    return lastYearMonth;
                                  }
                                };
                                return (
                                  <div key={index2} className="flex flex-col">
                                    <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                                      {`${
                                        item2?.creditor_name
                                          ? item2?.creditor_name
                                          : merge_label_key[item]
                                      } (-)`}
                                    </div>
                                    {retireData?.bf_plan_age?.map(
                                      (cash_flow_items, index3) => {
                                        return (
                                          <div
                                            key={index3}
                                            className={
                                              STYLE_WHITE_TABLE_RETIRE +
                                              " px-3 "
                                            }
                                          >
                                            {numeral(
                                              item2?.period === "Infinity"
                                                ? item2?.pay_permonth * 12
                                                : item2?.pay_permonth *
                                                    calcPeriodInterest(index3)
                                            )?.format("0,0.00")}
                                          </div>
                                        );
                                      }
                                    )}
                                  </div>
                                );
                              });
                          }

                          // เงินกู้ระยะสั้น
                          if (item === "debt_shortterm") {
                            return balanceData?.debt_short_list
                              ?.find(
                                (item) =>
                                  item?.options_debt_short === "debt_shortterm"
                              )
                              ?.short_terms?.map((item2, index2) => {
                                const calcPeriodInterest = (index) => {
                                  let year = Math.floor(item2?.period / 12);
                                  let lastYearMonth = item2?.period % 12;

                                  if (index >= Math.ceil(item2?.period / 12)) {
                                    return 0;
                                  }
                                  if (index < year) {
                                    return 12;
                                  } else {
                                    return lastYearMonth;
                                  }
                                };
                                return (
                                  <div key={index2} className="flex flex-col">
                                    <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                                      {`${
                                        item2?.creditor_name
                                          ? item2?.creditor_name
                                          : merge_label_key[item]
                                      } (-)`}
                                    </div>
                                    {retireData?.bf_plan_age?.map(
                                      (cash_flow_items, index3) => {
                                        return (
                                          <div
                                            key={index3}
                                            className={
                                              STYLE_WHITE_TABLE_RETIRE +
                                              " px-3 "
                                            }
                                          >
                                            {numeral(
                                              item2?.period === "Infinity"
                                                ? item2?.pay_permonth * 12
                                                : item2?.pay_permonth *
                                                    calcPeriodInterest(index3)
                                            )?.format("0,0.00")}
                                          </div>
                                        );
                                      }
                                    )}
                                  </div>
                                );
                              });
                          }

                          // บัตรเครดิต
                          if (item === "debt_creditcard") {
                            return creditCardPeriod?.map((item2, index2) => {
                              const calcPeriodInterest = (index) => {
                                let period = item2?.creditor_items?.length;

                                let year = Math.floor(period / 12);
                                let lastYearMonth = period % 12;

                                if (index >= Math.ceil(period / 12)) {
                                  return 0;
                                }
                                if (index < year) {
                                  return 12;
                                } else {
                                  return lastYearMonth;
                                }
                              };

                              return (
                                <div key={index2} className="flex flex-col">
                                  <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                                    {`${
                                      item2?.creditor_name
                                        ? item2?.creditor_name
                                        : merge_label_key[item]
                                    } (-)`}
                                  </div>
                                  {retireData?.bf_plan_age?.map(
                                    (cash_flow_items, index3) => {
                                      return (
                                        <div
                                          key={index3}
                                          className={
                                            STYLE_WHITE_TABLE_RETIRE + " px-3 "
                                          }
                                        >
                                          {item2?.type_payment === "pay_full" &&
                                            numeral(
                                              index3 === 0
                                                ? item2?.pay_peryear
                                                : item2?.pay_permonth *
                                                    calcPeriodInterest(index3)
                                            )?.format("0,0.00")}
                                          {(item2?.type_payment ===
                                            "pay_minimum" ||
                                            item2?.type_payment ===
                                              "pay_other") &&
                                            numeral(
                                              item2?.creditor_items
                                                ?.slice(
                                                  12 * index3,
                                                  12 * (index3 + 1)
                                                )
                                                ?.reduce((acc, item) => {
                                                  return acc + item?.total_min;
                                                }, 0)
                                            )?.format("0,0.00")}
                                        </div>
                                      );
                                    }
                                  )}
                                </div>
                              );
                            });
                          }

                          // ========หนี้ระยะยาว=============================
                          // หนี้ระยะยาว รถ
                          if (item === "debt_car") {
                            return balanceData?.debt_long_list
                              ?.find(
                                (item) => item?.options_debt_long === "debt_car"
                              )
                              ?.debt_long_cars?.map((item2, index2) => {
                                const calcPeriodInterest = (index) => {
                                  let year = Math.floor(item2?.period / 12);
                                  let lastYearMonth = item2?.period % 12;

                                  if (index >= Math.ceil(item2?.period / 12)) {
                                    return 0;
                                  }
                                  if (index < year) {
                                    return 12;
                                  } else {
                                    return lastYearMonth;
                                  }
                                };
                                return (
                                  <div key={index2} className="flex flex-col">
                                    <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                                      {`${
                                        item2?.title_car
                                          ? item2?.title_car
                                          : merge_label_key[item]
                                      } (-)`}
                                    </div>
                                    {retireData?.bf_plan_age?.map(
                                      (cash_flow_items, index3) => {
                                        return (
                                          <div
                                            key={index3}
                                            className={
                                              STYLE_WHITE_TABLE_RETIRE +
                                              " px-3 "
                                            }
                                          >
                                            {numeral(
                                              item2?.period === "Infinity"
                                                ? item2?.pay_permonth * 12
                                                : item2?.pay_permonth *
                                                    calcPeriodInterest(index3)
                                            )?.format("0,0.00")}
                                          </div>
                                        );
                                      }
                                    )}
                                  </div>
                                );
                              });
                          }

                          // หนี้ระยะยาว บ้าน
                          if (item === "debt_place") {
                            return balanceData?.debt_long_list
                              ?.find(
                                (item) =>
                                  item?.options_debt_long === "debt_place"
                              )
                              ?.debt_long_places?.map((item2, index2) => {
                                const calcPeriodInterest = (index) => {
                                  if (index >= Math.ceil(item2?.period)) {
                                    return 0;
                                  }

                                  return 12;
                                };
                                return (
                                  <div key={index2} className="flex flex-col">
                                    <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                                      {`${
                                        item2?.address_place
                                          ? item2?.address_place
                                          : merge_label_key[item]
                                      } (-)`}
                                    </div>
                                    {retireData?.bf_plan_age?.map(
                                      (cash_flow_items, index3) => {
                                        return (
                                          <div
                                            key={index3}
                                            className={
                                              STYLE_WHITE_TABLE_RETIRE +
                                              " px-3 "
                                            }
                                          >
                                            {numeral(
                                              // item2?.period === "Infinity"
                                              // ? item2?.pay_permonth * 12
                                              item2?.pay_permonth *
                                                calcPeriodInterest(index3)
                                            )?.format("0,0.00")}
                                          </div>
                                        );
                                      }
                                    )}
                                  </div>
                                );
                              });
                          }

                          // หนี้ระยะยาว อื่นๆ
                          if (item === "debt_long_other") {
                            return balanceData?.debt_long_list
                              ?.find(
                                (item) =>
                                  item?.options_debt_long === "debt_long_other"
                              )
                              ?.debt_long_others?.map((item2, index2) => {
                                const calcPeriodInterest = (index) => {
                                  if (index >= Math.ceil(item2?.period)) {
                                    return 0;
                                  }

                                  return 12;
                                };
                                return (
                                  <div key={index2} className="flex flex-col">
                                    <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                                      {`${
                                        item2?.title
                                          ? item2?.title
                                          : merge_label_key[item]
                                      } (-)`}
                                    </div>
                                    {retireData?.bf_plan_age?.map(
                                      (cash_flow_items, index3) => {
                                        return (
                                          <div
                                            key={index3}
                                            className={
                                              STYLE_WHITE_TABLE_RETIRE +
                                              " px-3 "
                                            }
                                          >
                                            {numeral(
                                              item2?.pay_permonth *
                                                calcPeriodInterest(index3)
                                            )?.format("0,0.00")}
                                          </div>
                                        );
                                      }
                                    )}
                                  </div>
                                );
                              });
                          }
                        })}

                        {/* การศึกษาบุตร */}
                        {retireData?.ishavekids &&
                          educationData?.map((item, index) => {
                            return (
                              <div key={index} className="flex flex-col">
                                <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                                  {`${item?.name} (-)`}
                                </div>
                                {retireData?.bf_plan_age?.map(
                                  (cash_flow_items, index3) => {
                                    return (
                                      <div
                                        key={index3}
                                        className={
                                          STYLE_WHITE_TABLE_RETIRE + " px-3 "
                                        }
                                      >
                                        {numeral(
                                          item?.education_items?.[index3]
                                            ?.sum_under_education || 0
                                        )?.format("0,0.00")}
                                      </div>
                                    );
                                  }
                                )}
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className=" flex  justify-center mt-5">
                <MTButton
                  // loading={loading}
                  text="ย้อนกลับ"
                  color="border border-maincyan "
                  colortext="text-maincyan group-hover:text-white"
                  hovercolor="hover:bg-maincyan group "
                  onClick={() => {
                    // clearRetireData();
                    NavigateGoBack(navigate);
                  }}
                />
              </div>
            </div>
          </Container>
        ) : isAfterMode ? (
          <Container>
            {/*-------------------- ตารางหลังวางแผน --------------------*/}
            <div className={`${STYLE_MARGIN_TOP_PARENT} min-h-screen mb-20 `}>
              <HeaderTitle
                title="ตารางสรุปความเคลื่อนไหวการเงิน"
                color="bg-[#EAEAEA]"
                isGoback
                onClickGoBack={() => {
                  NavigateGoBack(navigate);
                }}
              />
              <div className=" w-full h-14 px-6 font-semibold text-mainblue flex items-center ">
                <div  className="mr-3">หลังวางแผน</div>
                <MTButton
                  onClick={exportAfterPlan}
                  flexnone="flex-none"
                  width="w-fit text-xs"
                  text="Excel"
                  iconleft
                  icon={
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g clip-path="url(#clip0_6711_51468)">
                        <path
                          d="M14.1733 8.54535H11.6287V7.27304H14.1733V8.54535ZM14.1733 9.27235H11.6287V10.5447H14.1733V9.27235ZM14.1733 3.27441H11.6287V4.5467H14.1733V3.27441ZM14.1733 5.27373H11.6287V6.54601H14.1733V5.27373ZM14.1733 11.2717H11.6287V12.544H14.1733V11.2717ZM15.9309 13.7799C15.8582 14.158 15.4038 14.167 15.1003 14.1798H9.44769V15.8156H8.31897L0 14.3615V1.64045L8.36806 0.18457H9.44769V1.63316H14.9058C15.213 1.64588 15.5511 1.62407 15.8182 1.80763C16.0054 2.07663 15.9873 2.41835 16 2.72732L15.9927 12.1895C15.9837 12.7184 16.0418 13.2583 15.9309 13.7799ZM6.66497 10.9282C6.16334 9.91035 5.65263 8.89976 5.15278 7.88191C5.64716 6.89135 6.13428 5.89716 6.61956 4.90295C6.20697 4.92295 5.79438 4.94838 5.38363 4.97748C5.07644 5.72448 4.71838 6.45151 4.47847 7.22398C4.25491 6.49513 3.95866 5.79354 3.68784 5.08288C3.28797 5.10469 2.88809 5.12832 2.48825 5.15195C2.90991 6.08257 3.35887 7.00038 3.76781 7.93645C3.28616 8.84523 2.83541 9.76676 2.36828 10.681C2.76631 10.6973 3.16438 10.7137 3.56241 10.7191C3.84597 9.99576 4.19856 9.29963 4.44575 8.5617C4.6675 9.35416 5.04372 10.0866 5.35272 10.8445C5.79075 10.8755 6.22694 10.9027 6.66497 10.9282ZM15.1331 2.49638H9.44769V3.27441H10.9017V4.5467H9.44769V5.27373H10.9017V6.54601H9.44769V7.27304H10.9017V8.54535H9.44769V9.27235H10.9017V10.5447H9.44769V11.2717H10.9017V12.544H9.44769V13.3845H15.1331V2.49638Z"
                          fill="white"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_6711_51468">
                          <rect width="16" height="16" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  }
                />
              </div>
              {/* //Head */}
              <div className=" px-6">
                <div className="flex w-full ">
                  <div className=" shadow-md rounded-tl-[20px] rounded-bl-[20px] shadow-gray-500 w-fit sticky z-10 ">
                    <div className="  bg-gray50 h-14 px-3 font-semibold text-mainblue rounded-t-[20px]  rounded-tr-none w-12 flex gap-[38px] items-center ">
                      <div>อายุ</div>
                    </div>
                    <div className="  ">
                      <div
                        className={`  flex  items-center text-lg font-medium text-mainblue w-12 h-full `}
                      >
                        <div className=" h-14 w-full">
                          {retireData?.af_plan_age?.map((row, index) => {
                            return (
                              <div
                                key={index}
                                className={` h-full flex justify-center items-center text-mainblue w-full ${colorStatusAF(
                                  index
                                )}`}
                              >
                                {row}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className=" w-full max-w-2xl overflow-hidden overflow-x-scroll  h-full rounded-tr-[20px] rounded-br-[20px] bg-gray50">
                    <div className={`w-fit  `}>
                      <div
                        className={` flex font-semibold text-mainblue items-start w-full 
                    `}
                      >
                        {/* สินทรัพย์เพื่อการลงทุนสุทธิ */}
                        <div className="">
                          <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                            สินทรัพย์เพื่อการลงทุนสุทธิ
                          </div>
                          {retireData?.af_plan_age?.map((row, index) => {
                            return (
                              <div
                                key={index}
                                className={` whitespace-nowrap h-14 flex justify-center items-center px-3 ${colorStatusAF(
                                  index
                                )}`}
                              >
                                {numeral(
                                  Number(
                                    investmentReturnCalAF?.[index]
                                      ?.asset_invest || 0
                                  )
                                ).format("0,0.00")}
                              </div>
                            );
                          })}
                        </div>

                        {/* รวมรายได้ */}
                        <div className="">
                          <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                            รวมรายได้
                          </div>
                          {retireData?.af_plan_age?.map((row, index) => {
                            return (
                              <div
                                key={index}
                                className={STYLE_WHITE_TABLE_RETIRE + " px-3 "}
                              >
                                {numeral(
                                  Number(
                                    retireData?.af_plan_income?.[index] || 0
                                  ) +
                                    Number(
                                      retireData?.af_plan_income_other?.[
                                        index
                                      ] || 0
                                    )
                                ).format("0,0.00")}
                              </div>
                            );
                          })}
                        </div>

                        {/* รายได้อื่นๆ */}
                        <div className="">
                          <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                            รายได้อื่นๆ
                          </div>
                          {retireData?.af_plan_age?.map((row, index) => {
                            return (
                              <div
                                key={index}
                                className={STYLE_WHITE_TABLE_RETIRE + " px-3 "}
                              >
                                {numeral(
                                  Number(
                                    retireData?.af_plan_income_other?.[index] ||
                                      0
                                  )
                                ).format("0,0.00")}
                              </div>
                            );
                          })}
                        </div>

                        {/* เงินเดือน */}
                        <div className="">
                          <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                            เงินเดือน
                          </div>
                          {retireData?.af_plan_income?.map((row, index) => {
                            return (
                              <div
                                key={index}
                                className={STYLE_WHITE_TABLE_RETIRE + " px-3 "}
                              >
                                {numeral(row).format("0,0.00")}
                              </div>
                            );
                          })}
                        </div>

                        {/* รวมรายจ่ายต่อปี */}
                        <div className="">
                          <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                            {userData?.is_premium
                              ? "รวมรายจ่ายต่อปี"
                              : "รายจ่าย"}
                          </div>
                          {retireData?.af_plan_age?.map((row, index) => {
                            return (
                              <div
                                key={index}
                                className={STYLE_WHITE_TABLE_RETIRE + " px-3 "}
                              >
                                {userData?.is_premium &&
                                  numeral(
                                    Number(
                                      outcomeUreliableCalAF?.[index] || 0
                                    ) +
                                      Number(
                                        outcomeReliableCalAF?.[index] || 0
                                      ) +
                                      Number(outcomeDebtAF?.[index] || 0) +
                                      (retireData?.ishavekids
                                        ? Number(
                                            outcomeChildEducation?.[index] || 0
                                          )
                                        : 0)
                                  ).format("0,0.00")}
                                {!userData?.is_premium &&
                                  numeral(
                                    0
                                    // OutcomeReliableCalAfter(retireData)[index]
                                  ).format("0,0.00")}
                              </div>
                            );
                          })}
                        </div>

                        {/* รายจ่ายผันแปร */}
                        {userData?.is_premium && (
                          <div className="">
                            <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                              รายจ่ายผันแปร
                            </div>
                            {outcomeUreliableCalAF?.map((row, index) => {
                              return (
                                <div
                                  key={index}
                                  className={
                                    STYLE_WHITE_TABLE_RETIRE + " px-3 "
                                  }
                                >
                                  {numeral(row).format("0,0.00")}
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* รายจ่ายคงที่ */}
                        {userData?.is_premium && (
                          <div className="">
                            <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                              รายจ่ายคงที่
                            </div>
                            {outcomeReliableCalAF?.map((row, index) => {
                              return (
                                <div
                                  key={index}
                                  className={
                                    STYLE_WHITE_TABLE_RETIRE + " px-3 "
                                  }
                                >
                                  {numeral(row).format("0,0.00")}
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* คงเหลือ */}
                        <div className="">
                          <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                            คงเหลือ
                          </div>
                          {retireData?.af_plan_income?.map((row, index) => {
                            return (
                              <div
                                key={index}
                                className={STYLE_WHITE_TABLE_RETIRE + " px-3 "}
                              >
                                {numeral(
                                  row +
                                    Number(
                                      retireData?.af_plan_income_other?.[
                                        index
                                      ] || 0
                                    ) -
                                    (Number(
                                      outcomeUreliableCalAF?.[index] || 0
                                    ) +
                                      Number(
                                        outcomeReliableCalAF?.[index] || 0
                                      ) +
                                      Number(outcomeDebtAF?.[index] || 0) +
                                      (retireData?.ishavekids
                                        ? Number(
                                            outcomeChildEducation?.[index] || 0
                                          )
                                        : 0))
                                ).format("0,0.00")}
                              </div>
                            );
                          })}
                        </div>

                        {/* ผลตอบแทนการลงทุน */}
                        <div className="">
                          <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                            ผลตอบแทนการลงทุน
                          </div>
                          {retireData?.af_plan_age?.map((row, index) => {
                            return (
                              <div
                                key={index}
                                className={STYLE_WHITE_TABLE_RETIRE + " px-3 "}
                              >
                                {numeral(
                                  Number(
                                    investmentReturnCalAF?.[index]
                                      ?.return_invest || 0
                                  )
                                ).format("0,0.00")}
                              </div>
                            );
                          })}
                        </div>

                        {merge_keys?.map((item, index) => {
                          // ========หนี้=============================

                          // หนี้ดอกลอย
                          if (item === "debt_shortterm_other") {
                            return balanceData?.debt_short_list
                              ?.find(
                                (item) =>
                                  item?.options_debt_short ===
                                  "debt_shortterm_other"
                              )
                              ?.short_term_others?.map((item2, index2) => {
                                const calcPeriodInterest = (index) => {
                                  let year = Math.floor(item2?.period / 12);
                                  let lastYearMonth = item2?.period % 12;

                                  if (index >= Math.ceil(item2?.period / 12)) {
                                    return 0;
                                  }
                                  if (index < year) {
                                    return 12;
                                  } else {
                                    return lastYearMonth;
                                  }
                                };
                                return (
                                  <div key={index2} className="flex flex-col">
                                    <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                                      {`${
                                        item2?.creditor_name
                                          ? item2?.creditor_name
                                          : merge_label_key[item]
                                      } (-)`}
                                    </div>
                                    {retireData?.af_plan_age?.map(
                                      (cash_flow_items, index3) => {
                                        return (
                                          <div
                                            key={index3}
                                            className={
                                              STYLE_WHITE_TABLE_RETIRE +
                                              " px-3 "
                                            }
                                          >
                                            {numeral(
                                              item2?.period === "Infinity"
                                                ? item2?.pay_permonth * 12
                                                : item2?.pay_permonth *
                                                    calcPeriodInterest(index3)
                                            )?.format("0,0.00")}
                                          </div>
                                        );
                                      }
                                    )}
                                  </div>
                                );
                              });
                          }

                          // หนี้ีนอกระบบ
                          if (item === "debt_informal_shortterm") {
                            return balanceData?.debt_short_list
                              ?.find(
                                (item) =>
                                  item?.options_debt_short ===
                                  "debt_informal_shortterm"
                              )
                              ?.informal_short_terms?.map((item2, index2) => {
                                const calcPeriodInterest = (index) => {
                                  let year = Math.floor(
                                    item2?.number_of_days / 365
                                  );
                                  let lastYearMonth =
                                    item2?.number_of_days % 30;

                                  if (
                                    index >=
                                    Math.ceil(item2?.number_of_days / 365)
                                  ) {
                                    return 0;
                                  }
                                  if (index < year) {
                                    return 365;
                                  } else {
                                    return lastYearMonth;
                                  }
                                };
                                return (
                                  <div key={index2} className="flex flex-col">
                                    <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                                      {`${
                                        item2?.creditor_name
                                          ? item2?.creditor_name
                                          : merge_label_key[item]
                                      } (-)`}
                                    </div>
                                    {retireData?.af_plan_age?.map(
                                      (cash_flow_items, index3) => {
                                        return (
                                          <div
                                            key={index3}
                                            className={
                                              STYLE_WHITE_TABLE_RETIRE +
                                              " px-3 "
                                            }
                                          >
                                            {numeral(
                                              item2?.period === "Infinity"
                                                ? item2?.pay_permonth * 12
                                                : item2?.pay_permonth *
                                                    calcPeriodInterest(index3)
                                            )?.format("0,0.00")}
                                          </div>
                                        );
                                      }
                                    )}
                                  </div>
                                );
                              });
                          }

                          // เงินกู้ระยะสั้น
                          if (item === "debt_shortterm") {
                            return balanceData?.debt_short_list
                              ?.find(
                                (item) =>
                                  item?.options_debt_short === "debt_shortterm"
                              )
                              ?.short_terms?.map((item2, index2) => {
                                const calcPeriodInterest = (index) => {
                                  let year = Math.floor(item2?.period / 12);
                                  let lastYearMonth = item2?.period % 12;

                                  if (index >= Math.ceil(item2?.period / 12)) {
                                    return 0;
                                  }
                                  if (index < year) {
                                    return 12;
                                  } else {
                                    return lastYearMonth;
                                  }
                                };
                                return (
                                  <div key={index2} className="flex flex-col">
                                    <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                                      {`${
                                        item2?.creditor_name
                                          ? item2?.creditor_name
                                          : merge_label_key[item]
                                      } (-)`}
                                    </div>
                                    {retireData?.af_plan_age?.map(
                                      (cash_flow_items, index3) => {
                                        return (
                                          <div
                                            key={index3}
                                            className={
                                              STYLE_WHITE_TABLE_RETIRE +
                                              " px-3 "
                                            }
                                          >
                                            {numeral(
                                              item2?.period === "Infinity"
                                                ? item2?.pay_permonth * 12
                                                : item2?.pay_permonth *
                                                    calcPeriodInterest(index3)
                                            )?.format("0,0.00")}
                                          </div>
                                        );
                                      }
                                    )}
                                  </div>
                                );
                              });
                          }

                          // บัตรเครดิต
                          if (item === "debt_creditcard") {
                            return creditCardPeriod?.map((item2, index2) => {
                              const calcPeriodInterest = (index) => {
                                let period = item2?.creditor_items?.length;
                                let year = Math.floor(period / 12);
                                let lastYearMonth = period % 12;

                                if (index >= Math.ceil(period / 12)) {
                                  return 0;
                                }
                                if (index < year) {
                                  return 12;
                                } else {
                                  return lastYearMonth;
                                }
                              };

                              return (
                                <div key={index2} className="flex flex-col">
                                  <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                                    {`${
                                      item2?.creditor_name
                                        ? item2?.creditor_name
                                        : merge_label_key[item]
                                    } (-)`}
                                  </div>
                                  {retireData?.af_plan_age?.map(
                                    (cash_flow_items, index3) => {
                                      return (
                                        <div
                                          key={index3}
                                          className={
                                            STYLE_WHITE_TABLE_RETIRE + " px-3 "
                                          }
                                        >
                                          {item2?.type_payment === "pay_full" &&
                                            numeral(
                                              index3 === 0
                                                ? item2?.pay_peryear
                                                : item2?.pay_permonth *
                                                    calcPeriodInterest(index3)
                                            )?.format("0,0.00")}
                                          {(item2?.type_payment ===
                                            "pay_minimum" ||
                                            item2?.type_payment ===
                                              "pay_other") &&
                                            numeral(
                                              item2?.creditor_items
                                                ?.slice(
                                                  12 * index3,
                                                  12 * (index3 + 1)
                                                )
                                                ?.reduce((acc, item) => {
                                                  return acc + item?.total_min;
                                                }, 0)
                                            )?.format("0,0.00")}
                                        </div>
                                      );
                                    }
                                  )}
                                </div>
                              );
                            });
                          }

                          // ========หนี้ระยะยาว=============================
                          // หนี้ระยะยาว รถ
                          if (item === "debt_car") {
                            return balanceData?.debt_long_list
                              ?.find(
                                (item) => item?.options_debt_long === "debt_car"
                              )
                              ?.debt_long_cars?.map((item2, index2) => {
                                const calcPeriodInterest = (index) => {
                                  let year = Math.floor(item2?.period / 12);
                                  let lastYearMonth = item2?.period % 12;

                                  if (index >= Math.ceil(item2?.period / 12)) {
                                    return 0;
                                  }
                                  if (index < year) {
                                    return 12;
                                  } else {
                                    return lastYearMonth;
                                  }
                                };
                                return (
                                  <div key={index2} className="flex flex-col">
                                    <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                                      {`${
                                        item2?.title_car
                                          ? item2?.title_car
                                          : merge_label_key[item]
                                      } (-)`}
                                    </div>
                                    {retireData?.af_plan_age?.map(
                                      (cash_flow_items, index3) => {
                                        return (
                                          <div
                                            key={index3}
                                            className={
                                              STYLE_WHITE_TABLE_RETIRE +
                                              " px-3 "
                                            }
                                          >
                                            {numeral(
                                              item2?.period === "Infinity"
                                                ? item2?.pay_permonth * 12
                                                : item2?.pay_permonth *
                                                    calcPeriodInterest(index3)
                                            )?.format("0,0.00")}
                                          </div>
                                        );
                                      }
                                    )}
                                  </div>
                                );
                              });
                          }

                          // หนี้ระยะยาว บ้าน
                          if (item === "debt_place") {
                            return balanceData?.debt_long_list
                              ?.find(
                                (item) =>
                                  item?.options_debt_long === "debt_place"
                              )
                              ?.debt_long_places?.map((item2, index2) => {
                                const calcPeriodInterest = (index) => {
                                  if (index >= Math.ceil(item2?.period)) {
                                    return 0;
                                  }

                                  return 12;
                                };
                                return (
                                  <div key={index2} className="flex flex-col">
                                    <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                                      {`${
                                        item2?.address_place
                                          ? item2?.address_place
                                          : merge_label_key[item]
                                      } (-)`}
                                    </div>
                                    {retireData?.af_plan_age?.map(
                                      (cash_flow_items, index3) => {
                                        return (
                                          <div
                                            key={index3}
                                            className={
                                              STYLE_WHITE_TABLE_RETIRE +
                                              " px-3 "
                                            }
                                          >
                                            {numeral(
                                              item2?.pay_permonth *
                                                calcPeriodInterest(index3)
                                            )?.format("0,0.00")}
                                          </div>
                                        );
                                      }
                                    )}
                                  </div>
                                );
                              });
                          }

                          // หนี้ระยะยาว อื่นๆ
                          if (item === "debt_long_other") {
                            return balanceData?.debt_long_list
                              ?.find(
                                (item) =>
                                  item?.options_debt_long === "debt_long_other"
                              )
                              ?.debt_long_others?.map((item2, index2) => {
                                const calcPeriodInterest = (index) => {
                                  if (index >= Math.ceil(item2?.period)) {
                                    return 0;
                                  }

                                  return 12;
                                };
                                return (
                                  <div key={index2} className="flex flex-col">
                                    <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                                      {`${
                                        item2?.title
                                          ? item2?.title
                                          : merge_label_key[item]
                                      } (-)`}
                                    </div>
                                    {retireData?.af_plan_age?.map(
                                      (cash_flow_items, index3) => {
                                        return (
                                          <div
                                            key={index3}
                                            className={
                                              STYLE_WHITE_TABLE_RETIRE +
                                              " px-3 "
                                            }
                                          >
                                            {numeral(
                                              item2?.pay_permonth *
                                                calcPeriodInterest(index3)
                                            )?.format("0,0.00")}
                                          </div>
                                        );
                                      }
                                    )}
                                  </div>
                                );
                              });
                          }
                        })}

                        {/* การศึกษาบุตร */}
                        {retireData?.ishavekids &&
                          educationData?.map((item, index) => {
                            return (
                              <div key={index} className="flex flex-col">
                                <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                                  {`${item?.name} (-)`}
                                </div>
                                {retireData?.af_plan_age?.map(
                                  (cash_flow_items, index3) => {
                                    return (
                                      <div
                                        key={index3}
                                        className={
                                          STYLE_WHITE_TABLE_RETIRE + " px-3 "
                                        }
                                      >
                                        {numeral(
                                          Number(
                                            item?.education_items?.[index3]
                                              ?.sum_under_education || 0
                                          )
                                        )?.format("0,0.00")}
                                      </div>
                                    );
                                  }
                                )}
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className=" flex  justify-center mt-5">
                <MTButton
                  text="ย้อนกลับ"
                  color="border border-maincyan "
                  colortext="text-maincyan group-hover:text-white"
                  hovercolor="hover:bg-maincyan group "
                  onClick={() => {
                    NavigateGoBack(navigate);
                  }}
                />
              </div>
            </div>
          </Container>
        ) : isSensitive ? (
          <Container>
            {/*-------------------- ตารางหลังวางแผน --------------------*/}
            <div className={`${STYLE_MARGIN_TOP_PARENT} min-h-screen mb-20 `}>
              <HeaderTitle
                title="ตารางสรุปความเคลื่อนไหวการเงิน"
                color="bg-[#EAEAEA]"
                isGoback
                onClickGoBack={() => {
                  NavigateGoBack(navigate);
                }}
              />
              <div className=" w-full h-14 px-6 font-semibold text-mainblue flex items-center ">
                <div className="mr-3">ทดสอบความอ่อนไหว</div>
                <MTButton
                  onClick={exportSensitivePlan}
                  flexnone="flex-none"
                  width="w-fit text-xs"
                  text="Excel"
                  iconleft
                  icon={
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g clip-path="url(#clip0_6711_51468)">
                        <path
                          d="M14.1733 8.54535H11.6287V7.27304H14.1733V8.54535ZM14.1733 9.27235H11.6287V10.5447H14.1733V9.27235ZM14.1733 3.27441H11.6287V4.5467H14.1733V3.27441ZM14.1733 5.27373H11.6287V6.54601H14.1733V5.27373ZM14.1733 11.2717H11.6287V12.544H14.1733V11.2717ZM15.9309 13.7799C15.8582 14.158 15.4038 14.167 15.1003 14.1798H9.44769V15.8156H8.31897L0 14.3615V1.64045L8.36806 0.18457H9.44769V1.63316H14.9058C15.213 1.64588 15.5511 1.62407 15.8182 1.80763C16.0054 2.07663 15.9873 2.41835 16 2.72732L15.9927 12.1895C15.9837 12.7184 16.0418 13.2583 15.9309 13.7799ZM6.66497 10.9282C6.16334 9.91035 5.65263 8.89976 5.15278 7.88191C5.64716 6.89135 6.13428 5.89716 6.61956 4.90295C6.20697 4.92295 5.79438 4.94838 5.38363 4.97748C5.07644 5.72448 4.71838 6.45151 4.47847 7.22398C4.25491 6.49513 3.95866 5.79354 3.68784 5.08288C3.28797 5.10469 2.88809 5.12832 2.48825 5.15195C2.90991 6.08257 3.35887 7.00038 3.76781 7.93645C3.28616 8.84523 2.83541 9.76676 2.36828 10.681C2.76631 10.6973 3.16438 10.7137 3.56241 10.7191C3.84597 9.99576 4.19856 9.29963 4.44575 8.5617C4.6675 9.35416 5.04372 10.0866 5.35272 10.8445C5.79075 10.8755 6.22694 10.9027 6.66497 10.9282ZM15.1331 2.49638H9.44769V3.27441H10.9017V4.5467H9.44769V5.27373H10.9017V6.54601H9.44769V7.27304H10.9017V8.54535H9.44769V9.27235H10.9017V10.5447H9.44769V11.2717H10.9017V12.544H9.44769V13.3845H15.1331V2.49638Z"
                          fill="white"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_6711_51468">
                          <rect width="16" height="16" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  }
                />
              </div>
              {/* //Head */}
              <div className=" px-6">
                <div className="flex w-full ">
                  <div className=" shadow-md rounded-tl-[20px] rounded-bl-[20px] shadow-gray-500 w-fit sticky z-10 ">
                    <div className="  bg-gray50 h-14 px-3 font-semibold text-mainblue rounded-t-[20px]  rounded-tr-none w-12 flex gap-[38px] items-center ">
                      <div>อายุ</div>
                    </div>
                    <div className="  ">
                      <div
                        className={`  flex  items-center text-lg font-medium text-mainblue w-12 h-full `}
                      >
                        <div className=" h-14 w-full">
                          {retireData?.af_plan_remain?.map((row, index) => {
                            return (
                              <div
                                key={index}
                                className={` h-full flex justify-center items-center text-mainblue w-full ${colorStatusSensitive(
                                  index
                                )}`}
                              >
                                {row?.age}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className=" w-full max-w-2xl overflow-hidden overflow-x-scroll  h-full rounded-tr-[20px] rounded-br-[20px] bg-gray50">
                    <div className={`w-fit  `}>
                      <div
                        className={` flex font-semibold text-mainblue items-start w-full `}
                      >
                        {/* สินทรัพย์เพื่อการลงทุนสุทธิ */}
                        <div className="">
                          <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                            สินทรัพย์เพื่อการลงทุนสุทธิ
                          </div>
                          {retireData?.af_plan_remain?.map((row, index) => {
                            return (
                              <div
                                key={index}
                                className={` whitespace-nowrap h-14 flex justify-center items-center px-3 ${colorStatusSensitive(
                                  index
                                )}`}
                              >
                                {numeral(
                                  investmentReturnCalSensitive?.[index]
                                    ?.asset_invest
                                ).format("0,0.00")}
                              </div>
                            );
                          })}
                        </div>

                        {/* รวมรายได้ */}
                        <div className="">
                          <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                            รวมรายได้
                          </div>
                          {retireData?.af_plan_age?.map((row, index) => {
                            return (
                              <div
                                key={index}
                                className={STYLE_WHITE_TABLE_RETIRE + " px-3 "}
                              >
                                {numeral(
                                  Number(
                                    retireData?.af_plan_income?.[index] || 0
                                  ) +
                                    Number(
                                      retireData?.af_plan_income_other?.[
                                        index
                                      ] || 0
                                    )
                                ).format("0,0.00")}
                              </div>
                            );
                          })}
                        </div>

                        {/* รายได้อื่นๆ */}
                        <div className="">
                          <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                            รายได้อื่นๆ
                          </div>
                          {retireData?.af_plan_age?.map((row, index) => {
                            return (
                              <div
                                key={index}
                                className={STYLE_WHITE_TABLE_RETIRE + " px-3 "}
                              >
                                {numeral(
                                  Number(
                                    retireData?.af_plan_income_other?.[index] ||
                                      0
                                  )
                                ).format("0,0.00")}
                              </div>
                            );
                          })}
                        </div>

                        {/* เงินเดือน */}
                        <div className="">
                          <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                            เงินเดือน
                          </div>
                          {retireData?.af_plan_income?.map((row, index) => {
                            return (
                              <div
                                key={index}
                                className={STYLE_WHITE_TABLE_RETIRE + " px-3 "}
                              >
                                {numeral(row).format("0,0.00")}
                              </div>
                            );
                          })}
                        </div>

                        {/* รวมรายจ่ายต่อปี */}
                        <div className="">
                          <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                            {userData?.is_premium
                              ? "รวมรายจ่ายต่อปี"
                              : "รายจ่าย"}
                          </div>
                          {retireData?.af_plan_age?.map((row, index) => {
                            const sumExpense = userData?.is_premium
                              ? outcomeUreliableCalAF[index] +
                                outcomeReliableCalAF[index] +
                                outcomeDebtAF[index] +
                                (retireData?.ishavekids
                                  ? outcomeChildEducation[index]
                                  : 0) +
                                (row === retireData?.age_current + 5 &&
                                retireData?.check_imagine_two
                                  ? 1000000
                                  : 0) +
                                (row === retireData?.age_current + 10 &&
                                retireData?.check_imagine_two
                                  ? 5000000
                                  : 0)
                              : 0;
                            return (
                              <div
                                key={index}
                                className={STYLE_WHITE_TABLE_RETIRE + " px-3 "}
                              >
                                {userData?.is_premium &&
                                  numeral(sumExpense).format("0,0.00")}
                                {!userData?.is_premium &&
                                  numeral(
                                    0
                                    // OutcomeReliableCalAfter(retireData)[index]
                                  ).format("0,0.00")}
                              </div>
                            );
                          })}
                        </div>

                        {/* รายจ่ายผันแปร */}
                        {userData?.is_premium && (
                          <div className="">
                            <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                              รายจ่ายผันแปร
                            </div>
                            {outcomeUreliableCalAF?.map((row, index) => {
                              return (
                                <div
                                  key={index}
                                  className={
                                    STYLE_WHITE_TABLE_RETIRE + " px-3 "
                                  }
                                >
                                  {numeral(row).format("0,0.00")}
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* รายจ่ายคงที่ */}
                        {userData?.is_premium && (
                          <div className="">
                            <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                              รายจ่ายคงที่
                            </div>
                            {outcomeReliableCalAF?.map((row, index) => {
                              return (
                                <div
                                  key={index}
                                  className={
                                    STYLE_WHITE_TABLE_RETIRE + " px-3 "
                                  }
                                >
                                  {numeral(row).format("0,0.00")}
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* คงเหลือ */}
                        <div className="">
                          <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                            คงเหลือ
                          </div>
                          {retireData?.af_plan_income?.map((row, index) => {
                            // console.log("row",row);
                            return (
                              <div
                                key={index}
                                className={STYLE_WHITE_TABLE_RETIRE + " px-3 "}
                              >
                                {numeral(
                                  row +
                                    Number(
                                      retireData?.af_plan_income_other?.[
                                        index
                                      ] || 0
                                    ) -
                                    (outcomeUreliableCalAF[index] +
                                      outcomeReliableCalAF[index] +
                                      outcomeDebtAF[index] +
                                      (retireData?.ishavekids
                                        ? outcomeChildEducation[index]
                                        : 0))
                                ).format("0,0.00")}
                              </div>
                            );
                          })}
                        </div>

                        {/* here */}
                        {/* ผลตอบแทนการลงทุน */}
                        <div className="">
                          <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                            ผลตอบแทนการลงทุน
                          </div>
                          {retireData?.af_plan_age?.map((row, index) => {
                            return (
                              <div
                                key={index}
                                className={STYLE_WHITE_TABLE_RETIRE + " px-3 "}
                              >
                                {numeral(
                                  investmentReturnCalSensitive?.[index]
                                    ?.return_invest
                                ).format("0,0.00")}
                              </div>
                            );
                          })}
                        </div>

                        {merge_keys?.map((item, index) => {
                          // ========หนี้=============================

                          // หนี้ดอกลอย
                          if (item === "debt_shortterm_other") {
                            return balanceData?.debt_short_list
                              ?.find(
                                (item) =>
                                  item?.options_debt_short ===
                                  "debt_shortterm_other"
                              )
                              ?.short_term_others?.map((item2, index2) => {
                                const calcPeriodInterest = (index) => {
                                  let year = Math.floor(item2?.period / 12);
                                  let lastYearMonth = item2?.period % 12;

                                  if (index >= Math.ceil(item2?.period / 12)) {
                                    return 0;
                                  }
                                  if (index < year) {
                                    return 12;
                                  } else {
                                    return lastYearMonth;
                                  }
                                };
                                return (
                                  <div key={index2} className="flex flex-col">
                                    <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                                      {`${
                                        item2?.creditor_name
                                          ? item2?.creditor_name
                                          : merge_label_key[item]
                                      } (-) `}
                                    </div>
                                    {retireData?.af_plan_age?.map(
                                      (cash_flow_items, index3) => {
                                        return (
                                          <div
                                            key={index3}
                                            className={
                                              STYLE_WHITE_TABLE_RETIRE +
                                              " px-3 "
                                            }
                                          >
                                            {numeral(
                                              item2?.period === "Infinity"
                                                ? item2?.pay_permonth * 12
                                                : item2?.pay_permonth *
                                                    calcPeriodInterest(index3)
                                            )?.format("0,0.00")}
                                          </div>
                                        );
                                      }
                                    )}
                                  </div>
                                );
                              });
                          }

                          // หนี้ีนอกระบบ
                          if (item === "debt_informal_shortterm") {
                            return balanceData?.debt_short_list
                              ?.find(
                                (item) =>
                                  item?.options_debt_short ===
                                  "debt_informal_shortterm"
                              )
                              ?.informal_short_terms?.map((item2, index2) => {
                                const calcPeriodInterest = (index) => {
                                  let year = Math.floor(
                                    item2?.number_of_days / 365
                                  );
                                  let lastYearMonth =
                                    item2?.number_of_days % 30;

                                  if (
                                    index >=
                                    Math.ceil(item2?.number_of_days / 365)
                                  ) {
                                    return 0;
                                  }
                                  if (index < year) {
                                    return 365;
                                  } else {
                                    return lastYearMonth;
                                  }
                                };
                                return (
                                  <div key={index2} className="flex flex-col">
                                    <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                                      {`${
                                        item2?.creditor_name
                                          ? item2?.creditor_name
                                          : merge_label_key[item]
                                      } (-) `}
                                    </div>
                                    {retireData?.af_plan_age?.map(
                                      (cash_flow_items, index3) => {
                                        return (
                                          <div
                                            key={index3}
                                            className={
                                              STYLE_WHITE_TABLE_RETIRE +
                                              " px-3 "
                                            }
                                          >
                                            {numeral(
                                              item2?.period === "Infinity"
                                                ? item2?.pay_permonth * 12
                                                : item2?.pay_permonth *
                                                    calcPeriodInterest(index3)
                                            )?.format("0,0.00")}
                                          </div>
                                        );
                                      }
                                    )}
                                  </div>
                                );
                              });
                          }

                          // เงินกู้ระยะสั้น
                          if (item === "debt_shortterm") {
                            return balanceData?.debt_short_list
                              ?.find(
                                (item) =>
                                  item?.options_debt_short === "debt_shortterm"
                              )
                              ?.short_terms?.map((item2, index2) => {
                                const calcPeriodInterest = (index) => {
                                  let year = Math.floor(item2?.period / 12);
                                  let lastYearMonth = item2?.period % 12;

                                  if (index >= Math.ceil(item2?.period / 12)) {
                                    return 0;
                                  }
                                  if (index < year) {
                                    return 12;
                                  } else {
                                    return lastYearMonth;
                                  }
                                };
                                return (
                                  <div key={index2} className="flex flex-col">
                                    <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                                      {`${
                                        item2?.creditor_name
                                          ? item2?.creditor_name
                                          : merge_label_key[item]
                                      } (-) `}
                                    </div>
                                    {retireData?.af_plan_age?.map(
                                      (cash_flow_items, index3) => {
                                        return (
                                          <div
                                            key={index3}
                                            className={
                                              STYLE_WHITE_TABLE_RETIRE +
                                              " px-3 "
                                            }
                                          >
                                            {numeral(
                                              item2?.period === "Infinity"
                                                ? item2?.pay_permonth * 12
                                                : item2?.pay_permonth *
                                                    calcPeriodInterest(index3)
                                            )?.format("0,0.00")}
                                          </div>
                                        );
                                      }
                                    )}
                                  </div>
                                );
                              });
                          }

                          // บัตรเครดิต
                          if (item === "debt_creditcard") {
                            return creditCardPeriod?.map((item2, index2) => {
                              const calcPeriodInterest = (index) => {
                                let period = item2?.creditor_items?.length;
                                let year = Math.floor(period / 12);
                                let lastYearMonth = period % 12;

                                if (index >= Math.ceil(period / 12)) {
                                  return 0;
                                }
                                if (index < year) {
                                  return 12;
                                } else {
                                  return lastYearMonth;
                                }
                              };
                              return (
                                <div key={index2} className="flex flex-col">
                                  <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                                    {`${
                                      item2?.creditor_name
                                        ? item2?.creditor_name
                                        : merge_label_key[item]
                                    } (-)`}
                                  </div>
                                  {retireData?.af_plan_age?.map(
                                    (cash_flow_items, index3) => {
                                      return (
                                        <div
                                          key={index3}
                                          className={
                                            STYLE_WHITE_TABLE_RETIRE + " px-3 "
                                          }
                                        >
                                          {item2?.type_payment === "pay_full" &&
                                            numeral(
                                              index3 === 0
                                                ? item2?.pay_peryear
                                                : item2?.pay_permonth *
                                                    calcPeriodInterest(index3)
                                            )?.format("0,0.00")}
                                          {(item2?.type_payment ===
                                            "pay_minimum" ||
                                            item2?.type_payment ===
                                              "pay_other") &&
                                            numeral(
                                              item2?.creditor_items
                                                ?.slice(
                                                  12 * index3,
                                                  12 * (index3 + 1)
                                                )
                                                ?.reduce((acc, item) => {
                                                  return acc + item?.total_min;
                                                }, 0)
                                            )?.format("0,0.00")}
                                        </div>
                                      );
                                    }
                                  )}
                                </div>
                              );
                            });
                          }

                          // ========หนี้ระยะยาว=============================
                          // หนี้ระยะยาว รถ
                          if (item === "debt_car") {
                            return balanceData?.debt_long_list
                              ?.find(
                                (item) => item?.options_debt_long === "debt_car"
                              )
                              ?.debt_long_cars?.map((item2, index2) => {
                                const calcPeriodInterest = (index) => {
                                  let year = Math.floor(item2?.period / 12);
                                  let lastYearMonth = item2?.period % 12;

                                  if (index >= Math.ceil(item2?.period / 12)) {
                                    return 0;
                                  }
                                  if (index < year) {
                                    return 12;
                                  } else {
                                    return lastYearMonth;
                                  }
                                };
                                return (
                                  <div key={index2} className="flex flex-col">
                                    <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                                      {`${
                                        item2?.title_car
                                          ? item2?.title_car
                                          : merge_label_key[item]
                                      } (-)`}
                                    </div>
                                    {retireData?.af_plan_age?.map(
                                      (cash_flow_items, index3) => {
                                        return (
                                          <div
                                            key={index3}
                                            className={
                                              STYLE_WHITE_TABLE_RETIRE +
                                              " px-3 "
                                            }
                                          >
                                            {numeral(
                                              item2?.period === "Infinity"
                                                ? item2?.pay_permonth * 12
                                                : item2?.pay_permonth *
                                                    calcPeriodInterest(index3)
                                            )?.format("0,0.00")}
                                          </div>
                                        );
                                      }
                                    )}
                                  </div>
                                );
                              });
                          }

                          // หนี้ระยะยาว บ้าน
                          if (item === "debt_place") {
                            return balanceData?.debt_long_list
                              ?.find(
                                (item) =>
                                  item?.options_debt_long === "debt_place"
                              )
                              ?.debt_long_places?.map((item2, index2) => {
                                const calcPeriodInterest = (index) => {
                                  if (index >= Math.ceil(item2?.period)) {
                                    return 0;
                                  }

                                  return 12;
                                };
                                return (
                                  <div key={index2} className="flex flex-col">
                                    <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                                      {`${
                                        item2?.address_place
                                          ? item2?.address_place
                                          : merge_label_key[item]
                                      } (-)`}
                                    </div>
                                    {retireData?.af_plan_age?.map(
                                      (cash_flow_items, index3) => {
                                        return (
                                          <div
                                            key={index3}
                                            className={
                                              STYLE_WHITE_TABLE_RETIRE +
                                              " px-3 "
                                            }
                                          >
                                            {numeral(
                                              item2?.pay_permonth *
                                                calcPeriodInterest(index3)
                                            )?.format("0,0.00")}
                                          </div>
                                        );
                                      }
                                    )}
                                  </div>
                                );
                              });
                          }

                          // หนี้ระยะยาว อื่นๆ
                          if (item === "debt_long_other") {
                            return balanceData?.debt_long_list
                              ?.find(
                                (item) =>
                                  item?.options_debt_long === "debt_long_other"
                              )
                              ?.debt_long_others?.map((item2, index2) => {
                                const calcPeriodInterest = (index) => {
                                  if (index >= Math.ceil(item2?.period)) {
                                    return 0;
                                  }

                                  return 12;
                                };
                                return (
                                  <div key={index2} className="flex flex-col">
                                    <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                                      {`${
                                        item2?.title
                                          ? item2?.title
                                          : merge_label_key[item]
                                      } (-)`}
                                    </div>
                                    {retireData?.af_plan_age?.map(
                                      (cash_flow_items, index3) => {
                                        return (
                                          <div
                                            key={index3}
                                            className={
                                              STYLE_WHITE_TABLE_RETIRE +
                                              " px-3 "
                                            }
                                          >
                                            {numeral(
                                              item2?.pay_permonth *
                                                calcPeriodInterest(index3)
                                            )?.format("0,0.00")}
                                          </div>
                                        );
                                      }
                                    )}
                                  </div>
                                );
                              });
                          }
                        })}

                        {/* การศึกษาบุตร */}
                        {retireData?.ishavekids &&
                          educationData?.map((item, index) => {
                            return (
                              <div key={index} className="flex flex-col">
                                <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                                  {`${item?.name} (-)`}
                                </div>
                                {retireData?.af_plan_age?.map(
                                  (cash_flow_items, index3) => {
                                    return (
                                      <div
                                        key={index3}
                                        className={
                                          STYLE_WHITE_TABLE_RETIRE + " px-3 "
                                        }
                                      >
                                        {numeral(
                                          item?.education_items[index3]
                                            ?.sum_under_education
                                        )?.format("0,0.00")}
                                      </div>
                                    );
                                  }
                                )}
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className=" flex  justify-center mt-5">
                <MTButton
                  text="ย้อนกลับ"
                  color="border border-maincyan "
                  colortext="text-maincyan group-hover:text-white"
                  hovercolor="hover:bg-maincyan group "
                  onClick={() => {
                    NavigateGoBack(navigate);
                  }}
                />
              </div>
            </div>
          </Container>
        ) : (
          <Container>
            <div>Page Not Found.</div>
          </Container>
        )}
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      {isBeforeMode ? (
        <Container>
          {/*-------------------- ตารางก่อนวางแผน --------------------*/}
          <div className={`${STYLE_MARGIN_TOP_PARENT} min-h-screen mb-20 `}>
            <HeaderTitle
              title="ตารางสรุปความเคลื่อนไหวการเงิน"
              color="bg-[#EAEAEA]"
              isGoback
              onClickGoBack={() => {
                NavigateGoBack(navigate);
              }}
            />
            <div className=" w-full h-14 px-6 font-semibold text-mainblue flex items-center ">
              <div>ก่อนวางแผน</div>
            </div>
            {/* //Head */}
            <div className=" mt-8 px-6">
              <div className="flex w-full ">
                <div className=" shadow-md rounded-tl-[20px] rounded-bl-[20px] shadow-gray-500 w-fit sticky z-10 ">
                  <div className="  bg-gray50 h-14 px-3 font-semibold text-mainblue rounded-t-[20px]  rounded-tr-none w-12 flex gap-[38px] items-center ">
                    <div>อายุ</div>
                  </div>
                  <div className="  ">
                    <div
                      className={`  flex  items-center text-lg font-medium text-mainblue w-12 h-full `}
                    >
                      <div className=" h-14 w-full">
                        {dataAge()?.map((row, index) => {
                          return (
                            <div
                              key={index}
                              className={` h-full flex justify-center items-center text-mainblue w-full ${checkColor(
                                index
                              )}`}
                            >
                              {row}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
                <div className=" w-full max-w-2xl overflow-hidden overflow-x-scroll  h-full rounded-tr-[20px] rounded-br-[20px] bg-gray50">
                  <div className={`w-fit  `}>
                    <div
                      className={` flex font-semibold text-mainblue items-start w-full 
                  `}
                    >
                      {/* สินทรัพย์เพื่อการลงทุนสุทธิ */}
                      <div className="">
                        <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                          สินทรัพย์เพื่อการลงทุนสุทธิ
                        </div>
                        {InvestmentReturnNormalCal(retireData)?.map(
                          (row, index) => {
                            return (
                              <div
                                key={index}
                                className={` whitespace-nowrap h-14 flex justify-center items-center px-3 ${checkColor(
                                  index
                                )}`}
                              >
                                {numeral(row?.asset_invest).format("0,0.00")}
                              </div>
                            );
                          }
                        )}
                      </div>

                      {/* รายรับ */}
                      <div className="">
                        <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                          รายรับ
                        </div>
                        {retireData?.bf_plan_income?.map((row, index) => {
                          return (
                            <div
                              key={index}
                              className={STYLE_WHITE_TABLE_RETIRE + " px-3 "}
                            >
                              {numeral(row).format("0,0.00")}
                            </div>
                          );
                        })}
                      </div>

                      {/* รวมรายจ่ายต่อปี */}
                      <div className="">
                        <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                          {userData?.is_premium ? "รวมรายจ่ายต่อปี" : "รายจ่าย"}
                        </div>
                        {retireData?.bf_plan_age?.map((row, index) => {
                          return (
                            <div
                              key={index}
                              className={STYLE_WHITE_TABLE_RETIRE + " px-3 "}
                            >
                              {numeral(
                                OutcomeNormalCal(retireData, dataAge()[index])[
                                  index
                                ]
                              ).format("0,0.00")}
                            </div>
                          );
                        })}
                      </div>

                      {/* รายจ่ายผันแปร */}
                      {userData?.is_premium && (
                        <div className="">
                          <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                            รายจ่ายผันแปร
                          </div>
                          {OutcomeUreliableCal(retireData)?.map(
                            (row, index) => {
                              return (
                                <div
                                  key={index}
                                  className={
                                    STYLE_WHITE_TABLE_RETIRE + " px-3 "
                                  }
                                >
                                  {numeral(row).format("0,0.00")}
                                </div>
                              );
                            }
                          )}
                        </div>
                      )}

                      {/* รายจ่ายคงที่ */}
                      {userData?.is_premium && (
                        <div className="">
                          <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                            รายจ่ายคงที่
                          </div>
                          {outcomeReliableCal?.map((row, index) => {
                            return (
                              <div
                                key={index}
                                className={STYLE_WHITE_TABLE_RETIRE + " px-3 "}
                              >
                                {numeral(row).format("0,0.00")}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* คงเหลือ */}
                      <div className="">
                        <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                          คงเหลือ
                        </div>
                        {retireData?.bf_plan_income?.map((row, index) => {
                          return (
                            <div
                              key={index}
                              className={STYLE_WHITE_TABLE_RETIRE + " px-3 "}
                            >
                              {numeral(
                                row.toFixed(3) -
                                  OutcomeNormalCal(
                                    retireData,
                                    dataAge()[index]
                                  )[index].toFixed(3)
                              ).format("0,0.00")}
                            </div>
                          );
                        })}
                      </div>

                      {/* ผลตอบแทนการลงทุน */}
                      <div className="">
                        <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                          ผลตอบแทนการลงทุน
                        </div>
                        {retireData?.bf_plan_age?.map((row, index) => {
                          return (
                            <div
                              key={index}
                              className={STYLE_WHITE_TABLE_RETIRE + " px-3 "}
                            >
                              {numeral(
                                InvestmentReturnNormalCal(retireData)?.[index]
                                  ?.return_invest
                              ).format("0,0.00")}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className=" flex  justify-center mt-5">
              <MTButton
                // loading={loading}
                text="ย้อนกลับ"
                color="border border-maincyan "
                colortext="text-maincyan group-hover:text-white"
                hovercolor="hover:bg-maincyan group "
                onClick={() => {
                  // clearRetireData();
                  NavigateGoBack(navigate);
                }}
              />
            </div>
          </div>
        </Container>
      ) : isAfterMode ? (
        <Container>
          {/*-------------------- ตารางหลังวางแผน --------------------*/}
          <div className={`${STYLE_MARGIN_TOP_PARENT} min-h-screen mb-20 `}>
            <HeaderTitle
              title="ตารางสรุปความเคลื่อนไหวการเงิน"
              color="bg-[#EAEAEA]"
              isGoback
              onClickGoBack={() => {
                NavigateGoBack(navigate);
              }}
            />
            <div className=" w-full h-14 px-6 font-semibold text-mainblue flex items-center ">
              <div>หลังวางแผน</div>
            </div>
            {/* //Head */}
            <div className=" mt-8 px-6">
              <div className=" mt-8 px-6">
                <div className="flex w-full ">
                  <div className=" shadow-md rounded-tl-[20px] rounded-bl-[20px] shadow-gray-500 w-fit sticky z-10 ">
                    <div className="  bg-gray50 h-14 px-3 font-semibold text-mainblue rounded-t-[20px]  rounded-tr-none w-12 flex gap-[38px] items-center ">
                      <div>อายุ</div>
                    </div>
                    <div className="  ">
                      <div
                        className={`  flex  items-center text-lg font-medium text-mainblue w-12 h-full `}
                      >
                        <div className=" h-14 w-full">
                          {retireData?.af_plan_age?.map((row, index) => {
                            return (
                              <div
                                key={index}
                                className={` h-full flex justify-center items-center text-mainblue w-full ${checkColor(
                                  index
                                )}`}
                              >
                                {row}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className=" w-full max-w-2xl overflow-hidden overflow-x-scroll  h-full rounded-tr-[20px] rounded-br-[20px] bg-gray50">
                    <div className={`w-fit  `}>
                      <div
                        className={` flex font-semibold text-mainblue items-start w-full 
                  `}
                      >
                        {/* สินทรัพย์เพื่อการลงทุนสุทธิ */}
                        <div className="">
                          <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                            สินทรัพย์เพื่อการลงทุนสุทธิ
                          </div>
                          {retireData?.af_plan_age?.map((row, index) => {
                            return (
                              <div
                                key={index}
                                className={` whitespace-nowrap h-14 flex justify-center items-center px-3 ${checkColor(
                                  index
                                )}`}
                              >
                                {numeral(
                                  InvestmentReturnCalAfterNormal(
                                    retireData,
                                    strategy
                                  )?.[index]?.asset_invest
                                ).format("0,0.00")}
                              </div>
                            );
                          })}
                        </div>

                        {/* รายรับ */}
                        <div className="">
                          <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                            รายรับ
                          </div>
                          {retireData?.af_plan_income?.map((row, index) => {
                            return (
                              <div
                                key={index}
                                className={STYLE_WHITE_TABLE_RETIRE + " px-3 "}
                              >
                                {numeral(row).format("0,0.00")}
                              </div>
                            );
                          })}
                        </div>

                        {/* รวมรายจ่ายต่อปี */}
                        <div className="">
                          <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                            {userData?.is_premium
                              ? "รวมรายจ่ายต่อปี"
                              : "รายจ่าย"}
                          </div>
                          {retireData?.af_plan_age?.map((row, index) => {
                            return (
                              <div
                                key={index}
                                className={STYLE_WHITE_TABLE_RETIRE + " px-3 "}
                              >
                                {numeral(
                                  OutcomeUreliableCalAfterNormal(
                                    retireData,
                                    row,
                                    strategy
                                  )[index]
                                ).format("0,0.00")}
                              </div>
                            );
                          })}
                        </div>

                        {/* คงเหลือ */}
                        <div className="">
                          <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                            คงเหลือ
                          </div>
                          {retireData?.af_plan_income?.map((row, index) => {
                            return (
                              <div
                                key={index}
                                className={STYLE_WHITE_TABLE_RETIRE + " px-3 "}
                              >
                                {numeral(
                                  parseFloat(row).toFixed(3) -
                                    parseFloat(
                                      OutcomeUreliableCalAfterNormal(
                                        retireData,
                                        retireData?.af_plan_age[index],
                                        strategy
                                      )[index]
                                    ).toFixed(3)
                                ).format("0,0.00")}
                              </div>
                            );
                          })}
                        </div>

                        {/* ผลตอบแทนการลงทุน */}
                        <div className="">
                          <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                            ผลตอบแทนการลงทุน
                          </div>
                          {retireData?.af_plan_age?.map((row, index) => {
                            return (
                              <div
                                key={index}
                                className={STYLE_WHITE_TABLE_RETIRE + " px-3 "}
                              >
                                {numeral(
                                  InvestmentReturnCalAfterNormal(
                                    retireData,
                                    strategy
                                  )?.[index]?.return_invest
                                ).format("0,0.00")}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className=" flex  justify-center mt-5">
              <MTButton
                text="ย้อนกลับ"
                color="border border-maincyan "
                colortext="text-maincyan group-hover:text-white"
                hovercolor="hover:bg-maincyan group "
                onClick={() => {
                  NavigateGoBack(navigate);
                }}
              />
            </div>
          </div>
        </Container>
      ) : isSensitive ? (
        <Container>
          {/*-------------------- ตารางหลังวางแผน --------------------*/}
          <div className={`${STYLE_MARGIN_TOP_PARENT} min-h-screen mb-20 `}>
            <HeaderTitle
              title="ตารางสรุปความเคลื่อนไหวการเงิน"
              color="bg-[#EAEAEA]"
              isGoback
              onClickGoBack={() => {
                NavigateGoBack(navigate);
              }}
            />

            {/* //Head */}
            <div className=" mt-8 px-6">
              <div className=" mt-8 px-6">
                <div className="flex w-full ">
                  <div className=" shadow-md rounded-tl-[20px] rounded-bl-[20px] shadow-gray-500 w-fit sticky z-10 ">
                    <div className="  bg-gray50 h-14 px-3 font-semibold text-mainblue rounded-t-[20px]  rounded-tr-none w-12 flex gap-[38px] items-center ">
                      <div>อายุ</div>
                    </div>
                    <div className="  ">
                      <div
                        className={`  flex  items-center text-lg font-medium text-mainblue w-12 h-full `}
                      >
                        <div className=" h-14 w-full">
                          {retireData?.af_plan_remain?.map((row, index) => {
                            return (
                              <div
                                key={index}
                                className={` h-full flex justify-center items-center text-mainblue w-full ${checkColor(
                                  index
                                )}`}
                              >
                                {row?.age}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className=" w-full max-w-2xl overflow-hidden overflow-x-scroll  h-full rounded-tr-[20px] rounded-br-[20px] bg-gray50">
                    <div className={`w-fit  `}>
                      <div
                        className={` flex font-semibold text-mainblue items-start w-full 
                `}
                      >
                        {/* สินทรัพย์เพื่อการลงทุนสุทธิ */}
                        <div className="">
                          <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                            สินทรัพย์เพื่อการลงทุนสุทธิ
                          </div>
                          {retireData?.af_plan_remain?.map((row, index) => {
                            return (
                              <div
                                key={index}
                                className={` whitespace-nowrap h-14 flex justify-center items-center px-3 ${checkColor(
                                  index
                                )}`}
                              >
                                {numeral(
                                  InvestmentReturnCalAfterNormal(
                                    retireData,
                                    strategy
                                  )?.[index]?.asset_invest
                                ).format("0,0.00")}
                              </div>
                            );
                          })}
                        </div>

                        {/* รายรับ */}
                        <div className="">
                          <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                            รายรับ
                          </div>
                          {retireData?.af_plan_income?.map((row, index) => {
                            return (
                              <div
                                key={index}
                                className={STYLE_WHITE_TABLE_RETIRE + " px-3 "}
                              >
                                {numeral(row).format("0,0.00")}
                              </div>
                            );
                          })}
                        </div>

                        {/* รวมรายจ่ายต่อปี */}
                        <div className="">
                          <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                            {userData?.is_premium
                              ? "รวมรายจ่ายต่อปี"
                              : "รายจ่าย"}
                          </div>
                          {retireData?.af_plan_age?.map((row, index) => {
                            return (
                              <div
                                key={index}
                                className={STYLE_WHITE_TABLE_RETIRE + " px-3 "}
                              >
                                {numeral(
                                  OutcomeUreliableCalAfterNormal(
                                    retireData,
                                    row,
                                    strategy
                                  )[index]
                                ).format("0,0.00")}
                              </div>
                            );
                          })}
                        </div>

                        {/* คงเหลือ */}
                        <div className="">
                          <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                            คงเหลือ
                          </div>
                          {retireData?.af_plan_income?.map((row, index) => {
                            return (
                              <div
                                key={index}
                                className={STYLE_WHITE_TABLE_RETIRE + " px-3 "}
                              >
                                {numeral(
                                  parseFloat(row).toFixed(3) -
                                    parseFloat(
                                      OutcomeUreliableCalAfterNormal(
                                        retireData,
                                        retireData?.af_plan_age[index],
                                        strategy
                                      )[index]
                                    ).toFixed(3)
                                ).format("0,0.00")}
                              </div>
                            );
                          })}
                        </div>

                        {/* ผลตอบแทนการลงทุน */}
                        <div className="">
                          <div className=" h-14 text-sm pl-1 text-center md:break-words md:whitespace-nowrap break-word bg-gray50 flex justify-center items-center  md:pr-4 ">
                            ผลตอบแทนการลงทุน
                          </div>
                          {retireData?.af_plan_age?.map((row, index) => {
                            return (
                              <div
                                key={index}
                                className={STYLE_WHITE_TABLE_RETIRE + " px-3 "}
                              >
                                {numeral(
                                  InvestmentReturnCalAfterNormal(
                                    retireData,
                                    strategy
                                  )?.[index]?.return_invest
                                ).format("0,0.00")}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className=" flex  justify-center mt-5">
              <MTButton
                text="ย้อนกลับ"
                color="border border-maincyan "
                colortext="text-maincyan group-hover:text-white"
                hovercolor="hover:bg-maincyan group "
                onClick={() => {
                  NavigateGoBack(navigate);
                }}
              />
            </div>
          </div>
        </Container>
      ) : (
        <Container>
          <div>Page Not Found.</div>
        </Container>
      )}
    </React.Fragment>
  );
};

export default SummaryTableFinance;
